import './transfer-budget.scss';

import classnames from 'classnames';
import sum from 'lodash/sum';
import sumBy from 'lodash/sumBy';
import {
  bool,
  func,
  number,
  object,
  objectOf,
  shape,
  string,
} from 'prop-types';
import React, { useEffect } from 'react';

import { getSpentBudgetForProduct } from '../../campaign-editor/utils';
import CurrencyFormat from '../../generic/CurrencyFormat';
import { campaignShape } from '../../prop-types';
import { BUDGET_FIELDS, WRITABLE_BUDGET_FIELDS } from './constants';
import {
  calculateTotalDelta,
  checkIfDeltasMatch,
  hasDeltaChanged,
} from './utils';

const getTotalSpentBudget = (campaign, productAggregates) =>
  sum(
    productAggregates.map(({ product }) =>
      getSpentBudgetForProduct(campaign, product),
    ),
  );

export default function TransferBudgetTotals({
  productAggregatesMap,
  totalNewBudget,
  totalOldBudget,
  campaign,
  otherDeltas,
  onChange,
  sourceCampaign,
  totalTargetDelta,
}) {
  const productAggregates = Object.values(productAggregatesMap);
  const totalSpentBudget = getTotalSpentBudget(campaign, productAggregates);
  const totalLeftBudget = totalNewBudget - totalSpentBudget;
  const totalDelta = totalNewBudget - totalOldBudget;
  const allBudgets = Object.values(BUDGET_FIELDS);

  useEffect(() => {
    if (sourceCampaign) {
      const totalRoundedDelta =
        Math.round((totalDelta + totalTargetDelta) * 100) / 100;
      onChange({
        target: { name: 'delta', value: totalRoundedDelta },
      });
    }
  }, [onChange, totalDelta, totalTargetDelta, sourceCampaign]);
  return (
    <tr className="bg-gray-200">
      <td>Totals</td>
      <td>&nbsp;</td>
      {allBudgets.map(field => {
        const delta = calculateTotalDelta(productAggregates, field);
        const deltaChange = hasDeltaChanged(otherDeltas, field);
        const areDeltasMatching = checkIfDeltasMatch(delta, otherDeltas, field);

        return WRITABLE_BUDGET_FIELDS.includes(field)
          ? [
              <td key={`old-${field}`} className="text-right">
                <CurrencyFormat
                  name={`total-old-${field}`}
                  value={sumBy(productAggregates, `oldValues.${field}`)}
                  className="text-nowrap"
                  text
                />
              </td>,
              <td key={`new-${field}`} className="text-right">
                <CurrencyFormat
                  name={`total-new-${field}`}
                  value={sumBy(productAggregates, `newValues.${field}`)}
                  className="text-nowrap"
                  text
                />
              </td>,
              <td
                key={`delta-${field}`}
                className={classnames('text-right', {
                  'cr-bg-red': delta !== 0 && !areDeltasMatching,
                  'cr-bg-green': deltaChange && areDeltasMatching,
                })}
              >
                <CurrencyFormat
                  name={`total-delta-${field}`}
                  value={delta}
                  className={classnames('text-nowrap text-info', {
                    'cr-text-white': delta !== 0 && !areDeltasMatching,
                  })}
                  text
                />
              </td>,
            ]
          : [
              <td key={`old-${field}`} className="text-right">
                <CurrencyFormat
                  name={`total-old-${field}`}
                  value={sumBy(productAggregates, `oldValues.${field}`)}
                  className="text-nowrap"
                  text
                />
              </td>,
            ];
      })}
      <td className="text-right">
        <CurrencyFormat
          name="total-old-budget"
          value={totalOldBudget}
          className="text-nowrap"
          text
        />
      </td>
      <td className="text-right">
        <CurrencyFormat
          name="total-new-budget"
          value={totalNewBudget}
          className="text-nowrap"
          text
        />
      </td>
      <td className="text-right">
        <CurrencyFormat
          name="total-spent-budget"
          value={totalSpentBudget}
          className={classnames('text-danger', 'text-nowrap', {
            'bg-danger text-white': totalNewBudget < totalSpentBudget,
          })}
          text
        />
      </td>
      <td className="text-right">
        <CurrencyFormat
          name="total-left-budget"
          value={totalLeftBudget}
          className="text-nowrap text-success"
          text
        />
      </td>
      <td className="text-right">
        <CurrencyFormat
          name="total-delta"
          value={totalDelta}
          className="text-nowrap text-info"
          text
        />
      </td>
    </tr>
  );
}

const budgetFieldsShape = shape({
  netClientBudgetExternal: number.isRequired,
  wkzBudget: number.isRequired,
  coInvest: number.isRequired,
  freeService: number.isRequired,
});

TransferBudgetTotals.propTypes = {
  productAggregatesMap: objectOf(
    shape({
      label: string.isRequired,
      adjustedFee: number.isRequired,
      oldValues: budgetFieldsShape.isRequired,
      newValues: budgetFieldsShape.isRequired,
    }).isRequired,
  ).isRequired,
  totalNewBudget: number.isRequired,
  totalOldBudget: number.isRequired,
  campaign: campaignShape.isRequired,
  sourceCampaign: bool,
  onChange: func.isRequired,
  totalTargetDelta: number,
  otherDeltas: object, // eslint-disable-line react/forbid-prop-types
};

TransferBudgetTotals.defaultProps = {
  otherDeltas: undefined,
  sourceCampaign: false,
  totalTargetDelta: '',
};
